import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  VStack,
  HStack,
  Icon,
  Button,
  Progress,
  Badge,
  Divider,
  IconButton,
  useColorModeValue,
  SimpleGrid,
  Image,
  Center,
  Circle
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Play, Check, Activity, Heart, Brain, Users, FileText, Zap, Shield, TrendingUp, Calendar, MessageCircle } from 'lucide-react';

interface MobileResilienceActivityDetailProps {
  activityId: string;
  onBack: () => void;
}

const MobileResilienceActivityDetail: React.FC<MobileResilienceActivityDetailProps> = ({ 
  activityId,
  onBack 
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completed, setCompleted] = useState(false);
  
  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  
  // Activity data mapping
  const getActivityData = () => {
    switch (activityId) {
      case 'mindful-breathing':
        return {
          title: 'Mindful Breathing',
          duration: '2 minutes',
          description: 'A quick breathing exercise to reduce stress between patient visits.',
          steps: [
            'Find a comfortable seated position',
            'Close your eyes or maintain a soft gaze',
            'Breathe in slowly through your nose for 4 counts',
            'Hold your breath for 2 counts',
            'Exhale slowly through your mouth for 6 counts',
            'Repeat for 2 minutes'
          ],
          icon: Brain,
          color: 'blue',
          benefits: [
            'Reduces stress hormones',
            'Improves focus and concentration',
            'Can be done between patient visits',
            'Helps transition between tasks'
          ],
          relatedActivities: ['body-scan', 'stress-check'],
          completionMessage: 'Great job! This practice has been shown to reduce cortisol levels by up to 25% when done regularly.'
        };
      case 'quick-stretch':
        return {
          title: 'Quick Stretch',
          duration: '3 minutes',
          description: 'Stretches designed for clinicians who spend long hours standing or at a computer.',
          steps: [
            'Neck rolls: 5 in each direction',
            'Shoulder rolls: 5 in each direction',
            'Wrist stretches: 10 seconds each side',
            'Side bends: 5 each side',
            'Forward fold: 15 seconds',
            'Quad stretch: 10 seconds each leg'
          ],
          icon: Activity,
          color: 'orange',
          benefits: [
            'Reduces muscle tension',
            'Improves circulation',
            'Prevents repetitive strain injuries',
            'Can be done in scrubs or professional attire'
          ],
          relatedActivities: ['mindful-breathing', 'stress-check'],
          completionMessage: 'Excellent! Regular stretching can reduce muscle fatigue by up to 30% during long shifts.'
        };
      case 'peer-connection':
        return {
          title: 'Peer Connection',
          duration: '5 minutes',
          description: 'A structured check-in with a colleague to share challenges and successes.',
          steps: [
            'Choose a colleague to connect with',
            'Share one challenge you\'re facing (2 min)',
            'Share one success from your week (2 min)',
            'Identify one way you can support each other (1 min)'
          ],
          icon: Users,
          color: 'purple',
          benefits: [
            'Reduces feelings of isolation',
            'Creates support networks',
            'Normalizes challenges',
            'Builds team cohesion'
          ],
          relatedActivities: ['community-event', 'mentor-match'],
          completionMessage: 'Well done! Clinicians who engage in regular peer support report 40% higher job satisfaction.'
        };
      case 'clinical-reflection':
        return {
          title: 'Clinical Reflection',
          duration: '5 minutes',
          description: 'A structured reflection on a recent clinical case to enhance learning.',
          steps: [
            'Select a recent clinical case',
            'What went well? (1 min)',
            'What was challenging? (1 min)',
            'What would you do differently? (1 min)',
            'What did you learn? (1 min)',
            'How will you apply this learning? (1 min)'
          ],
          icon: FileText,
          color: 'teal',
          benefits: [
            'Enhances clinical reasoning',
            'Promotes continuous learning',
            'Builds confidence',
            'Improves patient care'
          ],
          relatedActivities: ['peer-connection', 'mentor-match'],
          completionMessage: 'Excellent reflection! Regular clinical reflection has been shown to reduce medical errors by up to 15%.'
        };
      case 'stress-check':
        return {
          title: 'Stress Management',
          duration: '3 minutes',
          description: 'A comprehensive stress assessment with personalized coping strategies tailored for healthcare professionals.',
          steps: [
            'Complete the quick stress assessment',
            'Identify your primary stressors',
            'Learn personalized coping techniques',
            'Create a micro-action plan',
            'Schedule a follow-up check-in'
          ],
          icon: Zap,
          color: 'orange',
          benefits: [
            'Evidence-based stress reduction',
            'Personalized to your practice environment',
            'Integrates with your clinical workflow',
            'Tracks progress over time'
          ],
          relatedActivities: ['mindful-breathing', 'quick-stretch'],
          completionMessage: 'Great work! Clinicians who use regular stress management techniques report 35% better patient interactions.',
          additionalContent: (
            <Box mt={4}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>Your Stress Profile</Text>
              <SimpleGrid columns={2} spacing={3} mb={3}>
                <Box p={3} borderRadius="md" bg="orange.50">
                  <HStack>
                    <Icon as={Shield} color="orange.500" />
                    <Text fontSize="xs" fontWeight="medium">Resilience Score</Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" mt={1}>72/100</Text>
                </Box>
                <Box p={3} borderRadius="md" bg="blue.50">
                  <HStack>
                    <Icon as={TrendingUp} color="blue.500" />
                    <Text fontSize="xs" fontWeight="medium">Recovery Rate</Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" mt={1}>81%</Text>
                </Box>
              </SimpleGrid>
              <Text fontSize="xs" mb={2}>Recommended Stress Management Techniques:</Text>
              <VStack align="stretch" spacing={2}>
                <HStack bg="gray.50" p={2} borderRadius="md">
                  <Icon as={Brain} color="blue.500" />
                  <Text fontSize="xs">Mindful Breathing (2 min)</Text>
                </HStack>
                <HStack bg="gray.50" p={2} borderRadius="md">
                  <Icon as={Activity} color="green.500" />
                  <Text fontSize="xs">Progressive Muscle Relaxation</Text>
                </HStack>
                <HStack bg="gray.50" p={2} borderRadius="md">
                  <Icon as={Clock} color="purple.500" />
                  <Text fontSize="xs">Time Blocking for Clinical Tasks</Text>
                </HStack>
              </VStack>
            </Box>
          )
        };
      case 'body-scan':
        return {
          title: 'Body Scan',
          duration: '3 minutes',
          description: 'A quick body awareness practice to release tension during your workday.',
          steps: [
            'Find a comfortable position sitting or standing',
            'Close your eyes if comfortable',
            'Bring awareness to your feet and notice any sensations',
            'Slowly move your attention up through your body',
            'Notice areas of tension without judgment',
            'Take a deep breath into any areas of tension'
          ],
          icon: Heart,
          color: 'pink',
          benefits: [
            'Identifies areas of physical tension',
            'Reduces physical manifestations of stress',
            'Improves body awareness',
            'Can be done between patient encounters'
          ],
          relatedActivities: ['mindful-breathing', 'quick-stretch'],
          completionMessage: 'Excellent awareness practice! Regular body scanning can reduce physical tension by up to 40%.'
        };
      case 'community-event':
        return {
          title: 'Community Events',
          duration: 'Varies',
          description: 'Connect with peers through upcoming wellness events and activities.',
          icon: Calendar,
          color: 'green',
          benefits: [
            'Builds supportive professional relationships',
            'Creates opportunities for mentorship',
            'Provides structured wellness activities',
            'Fosters sense of belonging'
          ],
          additionalContent: (
            <Box mt={4}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>Upcoming Events</Text>
              <VStack align="stretch" spacing={3}>
                <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Badge colorScheme="green" mb={1}>Next Week</Badge>
                  <Text fontSize="sm" fontWeight="medium">Wellness Wednesday Walk</Text>
                  <Text fontSize="xs" color={secondaryTextColor}>March 19, 12:00-1:00 PM</Text>
                  <Text fontSize="xs" mt={1}>Meet at the hospital garden for a guided mindful walking session with peers.</Text>
                  <Button size="xs" colorScheme="green" mt={2} leftIcon={<Icon as={Calendar} boxSize={3} />}>
                    Add to Calendar
                  </Button>
                </Box>
                <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Badge colorScheme="purple" mb={1}>This Month</Badge>
                  <Text fontSize="sm" fontWeight="medium">Peer Support Circle</Text>
                  <Text fontSize="xs" color={secondaryTextColor}>March 25, 5:30-6:30 PM</Text>
                  <Text fontSize="xs" mt={1}>Join fellow clinicians for a facilitated discussion on work-life integration.</Text>
                  <Button size="xs" colorScheme="purple" mt={2} leftIcon={<Icon as={Users} boxSize={3} />}>
                    Register
                  </Button>
                </Box>
                <Box p={3} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <Badge colorScheme="blue" mb={1}>Virtual</Badge>
                  <Text fontSize="sm" fontWeight="medium">Mindfulness for Clinicians</Text>
                  <Text fontSize="xs" color={secondaryTextColor}>On-demand</Text>
                  <Text fontSize="xs" mt={1}>Access guided mindfulness sessions designed specifically for healthcare professionals.</Text>
                  <Button size="xs" colorScheme="blue" mt={2} leftIcon={<Icon as={Play} boxSize={3} />}>
                    Watch Now
                  </Button>
                </Box>
              </VStack>
            </Box>
          )
        };
      case 'mentor-match':
        return {
          title: 'Peer Support Network',
          duration: 'Ongoing',
          description: 'Connect with colleagues for mutual support, mentorship, and community.',
          icon: MessageCircle,
          color: 'purple',
          benefits: [
            'Reduces professional isolation',
            'Provides emotional support',
            'Shares clinical wisdom and experience',
            'Creates lasting professional relationships'
          ],
          additionalContent: (
            <Box mt={4}>
              <Text fontSize="sm" fontWeight="medium" mb={2}>Your Support Network</Text>
              <SimpleGrid columns={2} spacing={3} mb={3}>
                <Box p={3} borderRadius="md" bg="purple.50">
                  <HStack>
                    <Icon as={Users} color="purple.500" />
                    <Text fontSize="xs" fontWeight="medium">Active Connections</Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" mt={1}>3</Text>
                </Box>
                <Box p={3} borderRadius="md" bg="green.50">
                  <HStack>
                    <Icon as={MessageCircle} color="green.500" />
                    <Text fontSize="xs" fontWeight="medium">Support Sessions</Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="bold" mt={1}>5</Text>
                </Box>
              </SimpleGrid>
              
              <Text fontSize="sm" fontWeight="medium" mt={4} mb={2}>Your Peer Connections</Text>
              <VStack align="stretch" spacing={2}>
                <HStack bg="gray.50" p={2} borderRadius="md" justify="space-between">
                  <HStack>
                    <Center bg="purple.100" borderRadius="full" boxSize="30px">
                      <Text fontSize="xs" fontWeight="bold">JD</Text>
                    </Center>
                    <VStack spacing={0} align="flex-start">
                      <Text fontSize="xs" fontWeight="medium">Dr. Jane Davis</Text>
                      <Text fontSize="2xs" color={secondaryTextColor}>Cardiology</Text>
                    </VStack>
                  </HStack>
                  <Button size="xs" colorScheme="purple" variant="outline">
                    Message
                  </Button>
                </HStack>
                
                <HStack bg="gray.50" p={2} borderRadius="md" justify="space-between">
                  <HStack>
                    <Center bg="blue.100" borderRadius="full" boxSize="30px">
                      <Text fontSize="xs" fontWeight="bold">RM</Text>
                    </Center>
                    <VStack spacing={0} align="flex-start">
                      <Text fontSize="xs" fontWeight="medium">Dr. Robert Miller</Text>
                      <Text fontSize="2xs" color={secondaryTextColor}>Emergency Medicine</Text>
                    </VStack>
                  </HStack>
                  <Button size="xs" colorScheme="blue" variant="outline">
                    Message
                  </Button>
                </HStack>
                
                <HStack bg="gray.50" p={2} borderRadius="md" justify="space-between">
                  <HStack>
                    <Center bg="green.100" borderRadius="full" boxSize="30px">
                      <Text fontSize="xs" fontWeight="bold">SP</Text>
                    </Center>
                    <VStack spacing={0} align="flex-start">
                      <Text fontSize="xs" fontWeight="medium">Dr. Sarah Patel</Text>
                      <Text fontSize="2xs" color={secondaryTextColor}>Family Medicine</Text>
                    </VStack>
                  </HStack>
                  <Button size="xs" colorScheme="green" variant="outline">
                    Message
                  </Button>
                </HStack>
              </VStack>
              
              <Button mt={4} size="sm" colorScheme="purple" width="full" leftIcon={<Icon as={Users} />}>
                Find More Peers
              </Button>
            </Box>
          )
        };
      case 'gratitude-journal':
        return {
          title: 'Gratitude Journal',
          duration: '5 minutes',
          description: 'A daily practice of recording things you are grateful for to improve mental wellbeing and resilience.',
          steps: [
            'Find a quiet moment in your day',
            'Reflect on three things you are grateful for today',
            'Write down each item and why it matters to you',
            'Note how these positive elements affect your mood',
            'Review previous entries periodically to reinforce positive patterns'
          ],
          icon: Heart,
          color: 'pink',
          benefits: [
            'Increases positive emotions',
            'Reduces burnout symptoms',
            'Improves sleep quality',
            'Enhances resilience to stress'
          ],
          relatedActivities: ['mindful-breathing', 'clinical-reflection'],
          completionMessage: 'Wonderful! Healthcare professionals who practice gratitude journaling report 35% higher resilience scores and improved work satisfaction.'
        };
      default:
        return {
          title: 'Mindful Breathing',
          duration: '2 minutes',
          description: 'A quick breathing exercise to reduce stress between patient visits.',
          steps: [
            'Find a comfortable seated position',
            'Close your eyes or maintain a soft gaze',
            'Breathe in slowly through your nose for 4 counts',
            'Hold your breath for 2 counts',
            'Exhale slowly through your mouth for 6 counts',
            'Repeat for 2 minutes'
          ],
          icon: Brain,
          color: 'blue',
          benefits: [
            'Reduces stress hormones',
            'Improves focus and concentration',
            'Can be done between patient visits',
            'Helps transition between tasks'
          ]
        };
    }
  };
  
  const activity = getActivityData();
  const totalSteps = activity.steps ? activity.steps.length : 0;
  
  const handleNextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCompleted(true);
    }
  };
  
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleReset = () => {
    setCurrentStep(0);
    setCompleted(false);
  };
  
  return (
    <Box p={4} pt={6}>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={4}>
        <IconButton
          aria-label="Go back"
          icon={<ArrowLeft />}
          variant="ghost"
          size="sm"
          onClick={onBack}
        />
        <Badge colorScheme={activity.color} variant="subtle" fontSize="xs">
          <Icon as={Clock} boxSize={3} mr={1} />
          {activity.duration}
        </Badge>
      </Flex>
      
      {/* Title */}
      <Flex 
        align="center" 
        mb={4}
        as={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 } as any}
      >
        <Circle size="40px" bg={`${activity.color}.100`} color={`${activity.color}.500`} mr={3}>
          <Icon as={activity.icon} boxSize={5} />
        </Circle>
        <Box>
          <Heading size="md">{activity.title}</Heading>
          <Text fontSize="xs" color={secondaryTextColor} mt={0}>
            {activity.description}
          </Text>
        </Box>
      </Flex>
      
      {!completed ? (
        <>
          {/* Progress */}
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontSize="xs" color={secondaryTextColor}>
              Step {currentStep + 1} of {totalSteps}
            </Text>
            <Text fontSize="xs" fontWeight="medium" color={`${activity.color}.500`}>
              {Math.round(((currentStep + 1) / totalSteps) * 100)}% Complete
            </Text>
          </Flex>
          <Progress 
            value={((currentStep + 1) / totalSteps) * 100} 
            size="sm" 
            colorScheme={activity.color} 
            borderRadius="full"
            mb={6}
          />
          
          {/* Step Content */}
          <Box 
            p={5} 
            bg={cardBg} 
            borderRadius="lg" 
            borderWidth="1px" 
            borderColor={borderColor}
            mb={6}
            as={motion.div}
            key={currentStep}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 } as any}
          >
            <Text fontSize="sm" fontWeight="medium" mb={4}>
              {activity.steps[currentStep]}
            </Text>
            
            {/* Timer or visual aid could go here */}
            <Box 
              height="150px" 
              bg={`${activity.color}.50`} 
              borderRadius="md" 
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={4}
            >
              <Icon as={activity.icon} boxSize={12} color={`${activity.color}.500`} opacity={0.8} />
            </Box>
            
            <Text fontSize="xs" color={secondaryTextColor}>
              Focus on this step for a few moments before continuing.
            </Text>
          </Box>
          
          {/* Navigation Buttons */}
          <HStack spacing={4} mb={6}>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ArrowLeft size={16} />}
              onClick={handlePrevStep}
              isDisabled={currentStep === 0}
              flex={1}
            >
              Previous
            </Button>
            <Button
              colorScheme={activity.color}
              size="sm"
              rightIcon={currentStep < totalSteps - 1 ? <Play size={16} /> : <Check size={16} />}
              onClick={handleNextStep}
              flex={2}
            >
              {currentStep < totalSteps - 1 ? 'Next Step' : 'Complete'}
            </Button>
          </HStack>
        </>
      ) : (
        <>
          {/* Completion Screen */}
          <Box 
            p={5} 
            bg={cardBg} 
            borderRadius="lg" 
            borderWidth="1px" 
            borderColor={borderColor}
            mb={6}
            as={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 } as any}
          >
            <VStack spacing={4} align="center">
              <Circle size="60px" bg={`${activity.color}.100`} color={`${activity.color}.500`}>
                <Icon as={Check} boxSize={8} />
              </Circle>
              <Heading size="md">Activity Completed!</Heading>
              <Text fontSize="sm" textAlign="center" color={textColor}>
                {activity.completionMessage || 'Great job completing this wellness activity. How do you feel?'}
              </Text>
              <Badge colorScheme="green" p={2} borderRadius="full">
                +10 Wellness Points
              </Badge>
            </VStack>
          </Box>
          
          {/* Benefits */}
          <Text fontSize="sm" fontWeight="medium" mb={2}>Benefits of this Activity</Text>
          <SimpleGrid columns={2} spacing={3} mb={6}>
            {activity.benefits.map((benefit, index) => (
              <Box 
                key={index}
                p={3} 
                bg={`${activity.color}.50`} 
                borderRadius="md"
                wordBreak="break-word"
                overflowWrap="break-word"
                whiteSpace="normal"
              >
                <HStack spacing={2} mb={1}>
                  <Icon as={Check} boxSize={3} color={`${activity.color}.500`} />
                  <Text fontSize="2xs" fontWeight="medium" color={`${activity.color}.700`}>
                    BENEFIT
                  </Text>
                </HStack>
                <Text fontSize="2xs">{benefit}</Text>
              </Box>
            ))}
          </SimpleGrid>
          
          {/* Additional content specific to the activity */}
          {activity.additionalContent}
          
          {/* Related Activities */}
          {activity.relatedActivities && (
            <>
              <Text fontSize="sm" fontWeight="medium" mt={6} mb={2}>Related Activities</Text>
              <HStack spacing={3} overflowX="auto" pb={2} css={{ '&::-webkit-scrollbar': { display: 'none' } }}>
                {activity.relatedActivities.map((relatedId, index) => {
                  const relatedActivity = getActivityData();
                  return (
                    <Button
                      key={index}
                      leftIcon={<Icon as={activity.icon} />}
                      colorScheme={activity.color.split('.')[0]}
                      variant="outline"
                      size="sm"
                      borderRadius="full"
                      flexShrink={0}
                      onClick={() => {
                        setCurrentStep(0);
                        setCompleted(false);
                      }}
                    >
                      Try Related Activity
                    </Button>
                  );
                })}
              </HStack>
            </>
          )}
          
          {/* Action Buttons */}
          <HStack spacing={4} mt={6}>
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ArrowLeft size={16} />}
              onClick={onBack}
              flex={1}
            >
              Back to Home
            </Button>
            <Button
              colorScheme={activity.color}
              size="sm"
              rightIcon={<Play size={16} />}
              onClick={handleReset}
              flex={1}
            >
              Do Again
            </Button>
          </HStack>
        </>
      )}
    </Box>
  );
};

export default MobileResilienceActivityDetail;
